import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, MapPin, Shield, Camera } from 'lucide-react';
import { enrollSelfie } from '../api/enrollment';
import { updateLocation } from '../api/location';
import VideoEnrollment from '../components/VideoEnrollment';

function Onboarding({ user, onComplete }) {
  const navigate = useNavigate();
  const isReenroll = new URLSearchParams(window.location.search).get('reenroll') === 'true';
  
  // Steps: 0 = welcome, 1 = location, 2 = video enrollment
  const [step, setStep] = useState(isReenroll ? 1 : 0);
  const [locationGranted, setLocationGranted] = useState(false);
  const [enrollmentComplete, setEnrollmentComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Guard: if user is already enrolled and this isn't a re-enroll, redirect to feed
  useEffect(() => {
    if (user && user.enrollment_complete && !isReenroll) {
      navigate('/feed', { replace: true });
    }
  }, [user, isReenroll, navigate]);

  // Request location permission
  const requestLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        updateLocation(position.coords.latitude, position.coords.longitude);
        setLocationGranted(true);
        toast.success('Location access granted!');
        setStep(2); // Move to video enrollment
      },
      (error) => {
        toast.error('Location access is required for the app to work properly');
      },
      { enableHighAccuracy: true }
    );
  };

  // Handle video enrollment completion
  const handleEnrollmentComplete = async (capturedFrames) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      toast.loading('Uploading face data...', { id: 'enrollment' });

      // Submit each captured frame to the enrollment API
      let successCount = 0;
      const angles = ['front', 'left_profile', 'center', 'right_profile', 'right'];

      for (let i = 0; i < Math.min(capturedFrames.length, 5); i++) {
        const frame = capturedFrames[i];
        const imageBase64 = frame.data.split(',')[1]; // Remove data:image/jpeg;base64, prefix
        
        try {
          const result = await enrollSelfie(imageBase64, angles[i] || `angle_${i}`);
          if (result.success || result.face_id) {
            successCount++;
          }
        } catch (err) {
          // Continue with other frames
        }
      }

      toast.dismiss('enrollment');

      if (successCount >= 2) {
        toast.success(`Face enrolled successfully! (${successCount} angles captured)`);
        setEnrollmentComplete(true);
        
        // Complete onboarding
        await finishOnboarding();
      } else {
        toast.error('Could not enroll enough face angles. Please try again.');
        setIsSubmitting(false);
      }

    } catch (error) {
      toast.dismiss('enrollment');
      toast.error(error.message || 'Enrollment failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Finish onboarding and navigate
  const finishOnboarding = async () => {
    try {
      await onComplete();
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (isReenroll) {
        navigate('/settings', { replace: true });
      } else {
        navigate('/feed', { replace: true });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
  };

  // Step 0: Welcome screen
  if (step === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-gray-50">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-primary" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-800 mb-3">
            Welcome to I'm In It
          </h1>
          
          <p className="text-gray-600 mb-8">
            To ensure your photos are only shared with real people who were actually there, 
            we need to verify your identity with a quick face scan.
          </p>

          <div className="space-y-4 text-left bg-white p-4 rounded-xl border border-gray-100 mb-8">
            <div className="flex items-start gap-3">
              <Camera className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-gray-800">Video Face Scan</p>
                <p className="text-sm text-gray-500">Rotate your head to capture multiple angles</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-gray-800">Liveness Check</p>
                <p className="text-sm text-gray-500">Blink and pause to prove you're real</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-gray-800">Location Access</p>
                <p className="text-sm text-gray-500">Required for proximity-based sharing</p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setStep(1)}
            className="w-full py-3 px-6 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
            data-testid="get-started-btn"
          >
            Get Started
          </button>
          
          <p className="text-xs text-gray-400 mt-4">
            Your face data is processed securely and never shared
          </p>
        </div>
      </div>
    );
  }

  // Step 1: Location permission
  if (step === 1) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-gray-50">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <MapPin className="w-10 h-10 text-blue-500" />
          </div>
          
          <h2 className="text-xl font-bold text-gray-800 mb-3">
            Enable Location
          </h2>
          
          <p className="text-gray-600 mb-8">
            Location is required to verify that you were actually present when photos were taken. 
            Your location history stays on your device.
          </p>

          {locationGranted ? (
            <div className="flex items-center justify-center gap-2 text-green-600 mb-6">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Location enabled!</span>
            </div>
          ) : (
            <button
              onClick={requestLocation}
              className="w-full py-3 px-6 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors mb-4"
              data-testid="enable-location-btn"
            >
              Enable Location Access
            </button>
          )}

          {!locationGranted && (
            <button
              onClick={() => setStep(0)}
              className="text-gray-500 hover:text-gray-700"
            >
              ← Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // Step 2: Video enrollment with liveness detection
  if (step === 2) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-white to-gray-50">
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <VideoEnrollment
            onComplete={handleEnrollmentComplete}
            onCancel={() => setStep(1)}
          />
        </div>

        {/* Info footer */}
        <div className="p-4 text-center">
          <p className="text-xs text-gray-400">
            {isReenroll ? 'Re-enrolling your face data' : 'Step 2 of 2: Face enrollment'}
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default Onboarding;
